import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getLicenseExpirationReport } from '../services/licensing'
import type { LicenseExpirationReportResponse, OrgInReport } from '../types'
import { buildCsvRows, rowsToCsvString, triggerCsvDownload } from './license-report.utils'
import { LicenseCard } from './LicenseExpirationReportPage.parts'

const DEFAULT_EXPIRES_WITHIN_DAYS = 90

function OrgSection({
  org,
  expandedLicenses,
  onToggleLicense,
}: {
  org: OrgInReport
  expandedLicenses: Set<string>
  onToggleLicense: (id: string) => void
}): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="overflow-hidden rounded-lg border border-stone-200 bg-white dark:border-stone-700 dark:bg-stone-800">
      <div className="border-b border-stone-200 bg-stone-50 px-6 py-4 dark:border-stone-700 dark:bg-stone-800/50">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-stone-900 dark:text-stone-100">{org.name}</h3>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-stone-500 dark:text-stone-400">
              {t('licenseReport.activeSeatsOf', {
                active: org.summary.activeSeats,
                total: org.summary.totalSeats,
              })}
            </span>
            {org.summary.expiringWithin30Days > 0 && (
              <span className="text-amber-600 dark:text-amber-400">
                {t('licenseReport.expiringIn30Days', { count: org.summary.expiringWithin30Days })}
              </span>
            )}
            {org.summary.alreadyExpired > 0 && (
              <span className="text-red-600 dark:text-red-400">
                {t('licenseReport.countExpired', { count: org.summary.alreadyExpired })}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="space-y-3 p-4">
        {org.licenses.map((license) => (
          <LicenseCard
            key={license.id}
            license={license}
            isExpanded={expandedLicenses.has(license.id)}
            onToggle={() => {
              onToggleLicense(license.id)
            }}
          />
        ))}
      </div>
    </div>
  )
}

interface LicenseReportState {
  report: LicenseExpirationReportResponse | null
  loading: boolean
  error: string | null
}

function useLicenseReport(expiresWithinDays: number, includeExpired: boolean): LicenseReportState {
  const { t } = useTranslation('admin')
  const [report, setReport] = useState<LicenseExpirationReportResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const fetchReport = async (): Promise<void> => {
      setLoading(true)
      setError(null)
      try {
        const data = await getLicenseExpirationReport({ expiresWithinDays, includeExpired })
        if (!cancelled) setReport(data)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : t('licenseReport.loadError'))
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void fetchReport()
    return () => {
      cancelled = true
    }
  }, [expiresWithinDays, includeExpired, t])

  return { report, loading, error }
}

interface LicenseSummary {
  totalOrgs: number
  totalLicenses: number
  totalSeats: number
  activeSeats: number
  expiring30: number
  expiring90: number
  expired: number
}

function useLicenseSummary(report: LicenseExpirationReportResponse | null): LicenseSummary | null {
  return useMemo(() => {
    if (!report) return null
    return {
      totalOrgs: report.organisations.length,
      totalLicenses: report.organisations.reduce((sum, org) => sum + org.licenses.length, 0),
      totalSeats: report.organisations.reduce((sum, org) => sum + org.summary.totalSeats, 0),
      activeSeats: report.organisations.reduce((sum, org) => sum + org.summary.activeSeats, 0),
      expiring30: report.organisations.reduce(
        (sum, org) => sum + org.summary.expiringWithin30Days,
        0
      ),
      expiring90: report.organisations.reduce(
        (sum, org) => sum + org.summary.expiringWithin90Days,
        0
      ),
      expired: report.organisations.reduce((sum, org) => sum + org.summary.alreadyExpired, 0),
    }
  }, [report])
}

function SummaryGrid({ summary }: { summary: LicenseSummary | null }): React.ReactElement | null {
  const { t } = useTranslation('admin')
  if (!summary) return null
  return (
    <div className="grid grid-cols-7 gap-4">
      {(
        [
          { value: summary.totalOrgs, label: t('licenseReport.organisations'), style: 'neutral' },
          { value: summary.totalLicenses, label: t('licenseReport.licenses'), style: 'neutral' },
          { value: summary.activeSeats, label: t('licenseReport.activeSeats'), style: 'neutral' },
          { value: summary.totalSeats, label: t('licenseReport.totalSeats'), style: 'neutral' },
          { value: summary.expiring30, label: t('licenseReport.expiring30d'), style: 'amber' },
          { value: summary.expiring90, label: t('licenseReport.expiring90d'), style: 'amber' },
          { value: summary.expired, label: t('licenseReport.expired'), style: 'red' },
        ] as const
      ).map(({ value, label, style }) => {
        const isNeutral = style === 'neutral'
        const isAmber = style === 'amber'
        const cardCls = isNeutral
          ? 'bg-white dark:bg-stone-800 rounded-lg border border-stone-200 dark:border-stone-700 p-4 text-center'
          : isAmber
            ? 'bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800 p-4 text-center'
            : 'bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 p-4 text-center'
        const valueCls = isNeutral
          ? 'text-2xl font-semibold text-stone-900 dark:text-stone-100'
          : isAmber
            ? 'text-2xl font-semibold text-amber-600 dark:text-amber-400'
            : 'text-2xl font-semibold text-red-600 dark:text-red-400'
        const labelCls = isNeutral
          ? 'text-xs text-stone-500 dark:text-stone-400'
          : isAmber
            ? 'text-xs text-amber-700 dark:text-amber-300'
            : 'text-xs text-red-700 dark:text-red-300'
        return (
          <div key={label} className={cardCls}>
            <div className={valueCls}>{value}</div>
            <div className={labelCls}>{label}</div>
          </div>
        )
      })}
    </div>
  )
}

function ReportHeader({
  report,
  onExportCsv,
}: {
  report: LicenseExpirationReportResponse | null
  onExportCsv: () => void
}): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="flex items-center justify-between">
      <div>
        <h1 className="text-2xl font-semibold text-stone-900 dark:text-stone-100">
          {t('licenseReport.title')}
        </h1>
        <p className="mt-1 text-sm text-stone-600 dark:text-stone-400">
          {t('licenseReport.description')}
        </p>
      </div>
      <button
        onClick={onExportCsv}
        disabled={!report || report.organisations.length === 0}
        className="inline-flex items-center gap-2 rounded-lg bg-stone-100 px-4 py-2 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-200 disabled:opacity-50 dark:bg-stone-700 dark:text-stone-200 dark:hover:bg-stone-600"
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
          />
        </svg>
        {t('licenseReport.exportCsv')}
      </button>
    </div>
  )
}

function ReportFilters({
  expiresWithinDays,
  includeExpired,
  generatedAt,
  onDaysChange,
  onIncludeExpiredChange,
}: {
  expiresWithinDays: number
  includeExpired: boolean
  generatedAt: string | null
  onDaysChange: (days: number) => void
  onIncludeExpiredChange: (checked: boolean) => void
}): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="flex items-center gap-6 rounded-lg border border-stone-200 bg-white p-4 dark:border-stone-700 dark:bg-stone-800">
      <div className="flex items-center gap-2">
        <label className="text-sm text-stone-600 dark:text-stone-400">
          {t('licenseReport.expiringWithin')}
        </label>
        <select
          value={expiresWithinDays}
          onChange={(e) => {
            onDaysChange(parseInt(e.target.value))
          }}
          className="rounded-lg border border-stone-300 bg-white px-3 py-1.5 text-sm text-stone-900 dark:border-stone-600 dark:bg-stone-700 dark:text-stone-100"
        >
          <option value={30}>{t('licenseReport.filterDays', { count: 30 })}</option>
          <option value={60}>{t('licenseReport.filterDays', { count: 60 })}</option>
          <option value={90}>{t('licenseReport.filterDays', { count: 90 })}</option>
          <option value={180}>{t('licenseReport.filterDays', { count: 180 })}</option>
          <option value={365}>{t('licenseReport.filterYear')}</option>
        </select>
      </div>
      <label className="flex cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          checked={includeExpired}
          onChange={(e) => {
            onIncludeExpiredChange(e.target.checked)
          }}
          className="h-4 w-4 rounded border-stone-300 text-[#5c6e94] focus:ring-[#4e5e82] dark:border-stone-600"
        />
        <span className="text-sm text-stone-600 dark:text-stone-400">
          {t('licenseReport.includeExpired')}
        </span>
      </label>
      {generatedAt && (
        <div className="ml-auto text-xs text-stone-500 dark:text-stone-400">
          {t('licenseReport.generated')} {new Date(generatedAt).toLocaleString()}
        </div>
      )}
    </div>
  )
}

function ReportBody({
  loading,
  error,
  report,
  expandedLicenses,
  onToggleLicense,
}: {
  loading: boolean
  error: string | null
  report: LicenseExpirationReportResponse | null
  expandedLicenses: Set<string>
  onToggleLicense: (id: string) => void
}): React.ReactElement {
  const { t } = useTranslation('admin')
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <svg className="h-8 w-8 animate-spin text-[#5c6e94]" fill="none" viewBox="0 0 24 24">
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
    )
  }
  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
        <p className="text-red-700 dark:text-red-300">{error}</p>
      </div>
    )
  }
  if (!report) return <></>
  if (report.organisations.length === 0) {
    return (
      <div className="py-12 text-center text-stone-500 dark:text-stone-400">
        {t('licenseReport.noLicensesFound')}
      </div>
    )
  }
  return (
    <div className="space-y-6">
      {report.organisations.map((org) => (
        <OrgSection
          key={org.id}
          org={org}
          expandedLicenses={expandedLicenses}
          onToggleLicense={onToggleLicense}
        />
      ))}
    </div>
  )
}

export function LicenseExpirationReportPage(): React.ReactElement {
  const [expiresWithinDays, setExpiresWithinDays] = useState(DEFAULT_EXPIRES_WITHIN_DAYS)
  const [includeExpired, setIncludeExpired] = useState(false)
  const [expandedLicenses, setExpandedLicenses] = useState<Set<string>>(new Set())
  const { report, loading, error } = useLicenseReport(expiresWithinDays, includeExpired)
  const summary = useLicenseSummary(report)

  const handleToggleLicense = (id: string): void => {
    setExpandedLicenses((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleExportCsv = (): void => {
    if (!report) return
    const dateStr = new Date().toISOString().split('T')[0] ?? 'unknown'
    triggerCsvDownload(
      rowsToCsvString(buildCsvRows(report)),
      `license-expiration-report-${dateStr}.csv`
    )
  }

  return (
    <div className="space-y-6">
      <ReportHeader report={report} onExportCsv={handleExportCsv} />
      <ReportFilters
        expiresWithinDays={expiresWithinDays}
        includeExpired={includeExpired}
        generatedAt={report?.generatedAt ?? null}
        onDaysChange={setExpiresWithinDays}
        onIncludeExpiredChange={setIncludeExpired}
      />
      <SummaryGrid summary={summary} />
      <ReportBody
        loading={loading}
        error={error}
        report={report}
        expandedLicenses={expandedLicenses}
        onToggleLicense={handleToggleLicense}
      />
    </div>
  )
}
